import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { concatMap, forkJoin, from, Observable, Observer, timeout, timer } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthTokenStore } from '../../interceptors/auth-token.store';
import { UserProfile } from '../../models/user-profile';
import { UnitSettings } from '../../models/unit-settings';
import { RoutingSettings } from '../../models/routing-settings';
import { VoiceGuidenceSettings } from '../../models/voice-guidence-settings';
import { MapSettings } from '../../models/map-settings';
import { Address } from '../../models/address';
import { RangeParams } from '../../models/range-params';
import { ECarType } from '../../models/ecar-type';
import { Md5 } from 'ts-md5';
import { Position } from '../../models/position';
import { LogElementRange } from '../../models/log';
import { BillingAddress, Business, Product } from '../../models/billing';
import { DisplayMode } from '../../models/display-mode';
import { WidgetService } from '../platform/widget.service';

const VOICE_LIBS: string[] = ["de-DE_Marlene", "en-US_Kendra", "es-ES_Conchita", "fr-FR_Celine", "it-IT_Carla"];
const API_URL = environment.API_URL + '/api/';
const HTTP_TIMEOUT = 60000;
const languageJsonVersion = 7;

const LANGUAGE_JSON_URL = "assets/json/language_$lang.json";
const COUNTRYLIST_JSON_URL = "assets/json/countries_$lang.json";
const VERSION_URL = `${environment.BACKEND_URL}/chargingstations/getversion.php`;
const VEHICLES_VERSION_URL = `${API_URL}vehicles/last-changed-at`;
const ALL_VEHICLES_URL = `${API_URL}vehicles/all`;
const CHARGING_OPERATORS_URL = `${environment.TILE_SERVER_URL}/chargingstations/operators.json?ver=$ver`;
const TURN_AUDIO_URL = `assets/audio/turn/$voiceLib/Commands/$turnName`;
const REVERSE_GEOCODE_URL = `${environment.REVERSE_GEOCODE_URL}/routing/reversegeocode.php?lat=$lat&lon=$lon`;
const CHARGING_TILE_URL = `${environment.TILE_SERVER_URL}/dattiles/$typeFlag/$tileId.dat.$hash?ver=$chargingStationVersion`;
const CHECK_URL = `${environment.BACKEND_URL}/ecar/check.php`;
const SEND_FEEDBACK_EMAIL_URL = `${environment.BACKEND_URL}/mail/mail.php`;
const CHARGING_STATION_STATUS_URL = `${environment.BACKEND_URL}/ecar/get_chargingstation_status.php`;
const CHARGING_STATION_DETAIL_URL = `${environment.BACKEND_URL}/ecar/get_chargingstation_detail.php`;

const USER_COUNTRY_URL = API_URL + 'user/country';
const USER_URL = API_URL + 'user';
const USER_VEHICLES_URL = API_URL + 'user/vehicles';
const USER_SETTINGS_URL = API_URL + 'user/settings';
const USER_EXIST_URL = API_URL + 'user/exist';
const REGISTER_URL = API_URL + 'register';
const REGISTER_EXTERNAL = API_URL + "register_external";
const REGISTER_EXTERNAL_GOOGLE = API_URL + "register_external_gsi";
const RESEND_URL = API_URL + 'resend';
const LOGIN_URL = API_URL + 'login';
const USER_PROFILE_URL = API_URL + 'user/profile';
const USER_VEHICLE_ADD_URL = API_URL + 'user/vehicles/add';
const USER_VEHICLE_SELECT_URL = API_URL + 'user/vehicles/select';
const USER_VEHICLE_MODIFY_URL = API_URL + 'user/vehicles/modify';
const USER_VEHICLE_REMOVE_URL = API_URL + 'user/vehicles/remove';
const USER_CHANGE_PASSWORD_URL = API_URL + 'user/changepassword';
const RESET_REQUEST_URL = API_URL + 'reset/request';
const RESET_PASSWORD_URL = API_URL + 'password/reset';
const USER_ADDRESS_URL = API_URL + 'user/address';
const USER_DELETE_URL = API_URL + 'user/delete';
const USER_DELETE_CONFIRM_URL = API_URL + 'user/delete/confirm?deletetoken=$token';
const WIDGET_KEY_VALIDATION_URL = API_URL + 'isvalidkey';
const CANCEL_USER_SUBSCRIPTION = `${environment.API_URL}/stripe/cancel`;
const EVNAVI_GET_ACTIVITIES_URL = API_URL + 'evnavi/getActivities/$carId';
const EVNAVI_READ_FILES_URL = API_URL + 'evnavi/readfiles/$logId';
const EVNAVI_LOG_URL = API_URL + 'evnavi/log/$userVehicleId';
const EVNAVI_DELETE_LOG_BY_ID_URL = API_URL + 'evnavi/deletelogbyid/$logId';
const EVNAVI_DELETE_LOG_BY_VEHICLE_URL = API_URL + 'evnavi/deletelogbyvehicle/$carId';
const UPDATE_DISABLED_CHARGING_STATIONS = API_URL + "user/stations/disable";
const UPDATE_FAVORITE_CHARGING_STATIONS = API_URL + "user/stations/favorite";
const UPDATE_DISABLED_CHARGING_OPERATORS = API_URL + "user/operator/disable";
const UPDATE_FAVORITE_CHARGING_OPERATORS = API_URL + "user/operator/favorite";
const VOUCHER_CHECK = API_URL + "vouchers/check";
const ADD_VOUCHER_SUBSCRIPTION = API_URL + "vouchers/subscription";
const EXPORT_PERSONAL_DATA = `${environment.API_URL}/export-personal-data`;
const SUBSCRIBE_TO_SUBSCRIPTION = `${environment.API_URL}/save-billing-data`;
const VAT_CHECK = API_URL + "vat/check?vat_number=$vatNumber&country_code=$countryCode";

const RANGE_URL = API_URL + "rha";
const ROUTING_URL = API_URL + "routing";

export const VEHICLE_IMAGE_URL = `${environment.AMAZON_S3_BUCKET_URL}/vehicles/icons/$skin/$icon_name.svg`;
/** Local asset used when the S3 vehicle image is unreachable or missing. See VehicleImageFallbackDirective. */
export const VEHICLE_IMAGE_FALLBACK_URL = 'assets/icons/$skin/ecars/ecar_icon_default.svg';

@Injectable({
  providedIn: 'root'
})
export class HttpService {

  private readonly http = inject(HttpClient);
  private readonly tokenStore = inject(AuthTokenStore);
  private readonly widgetService = inject(WidgetService);

  public setToken(token: string) {
    this.tokenStore.set(token);
  }

  public getRange(rangeParams: RangeParams, selectedCar: ECarType, selectedDisplayMode: DisplayMode,  profileId: string, rangeLogs: LogElementRange[]) {
    const postData: any = {
      Params: { ...rangeParams, WindMan: 0 },
      Car: { ...selectedCar },
      History: rangeLogs
    };

    if (selectedDisplayMode == DisplayMode.ROUTE) {
      postData.Params.RangeInputParams.batterySafetyLimit = 50;
    }

    postData.Params.RangeInputParams.batterySafetyLimit = postData.Params.RangeInputParams.batterySafetyLimitRha;
    if (postData.Params.RangeInputParams.batteryStateOfHealth) {
      postData.Car.Range = postData.Car.FactoryRange * postData.Params.RangeInputParams.batteryStateOfHealth / 100;
    }

    /** remove car name from polyparams */
    ['Name', 'Subtype', 'Type'].forEach(prop => delete postData.Car[prop]);

    if (profileId) {
      postData.UserId = profileId;
    }

    this.addWidgetKey(postData);

    const VqrzrxFGyHNUVIRU = this.VqrzrxFGyHNUVIRU(postData);
    const headers = new HttpHeaders({
      'Vqrzrxfgyhnuviru': VqrzrxFGyHNUVIRU.toString()
    });

    return this.http.post(RANGE_URL, window.btoa(JSON.stringify(postData)), { headers: headers });
  }

  public getRouting(rangeParams: RangeParams, selectedCar: ECarType, disabledChargingStation: string[], preferedChargingStation: string[],
    disabledOperator: number[], preferedOperator: number[], selectedDisplayMode: DisplayMode, bearing: number, routingSettings: RoutingSettings,
    position: Position, profileId: string) {

    // Dynamically include only non-null and non-undefined params
    const optionalParams = {
      ...(disabledChargingStation && { DisabledChargingStation: disabledChargingStation }),
      ...(preferedChargingStation && { PreferedChargingStation: preferedChargingStation }),
      ...(disabledOperator && { DisabledOperator: disabledOperator }),
      ...(preferedOperator && { PreferedOperator: preferedOperator }),
      ...(bearing !== undefined && bearing !== null && { Bearing: bearing })
    };

    const postData: any = {
      Params: {
        ...rangeParams,
        WindMan: 0,
        ...optionalParams,
        RouteParams: {
          Tollroads: routingSettings.TollRoads ? 1 : 0,
          Ferries: routingSettings.Ferries ? 1 : 0,
          Highways: routingSettings.Highways ? 1 : 0
        }
      },
      Car: { ...selectedCar }
    };

    if (position) {
      postData.Params.Waypoints[0].lat = position.Latitude;
      postData.Params.Waypoints[0].lng = position.Longitude;
    }

    if (selectedDisplayMode == DisplayMode.RANGE) {
      postData.RangeInputParams.BatterySafetyLimit = 15;
    }

    postData.Params.RangeInputParams.batterySafetyLimit = postData.Params.RangeInputParams.batterySafetyLimitRoute;
    if (postData.Params.RangeInputParams.batteryStateOfHealth) {
      postData.Car.Range = postData.Car.FactoryRange * postData.Params.RangeInputParams.batteryStateOfHealth / 100;
    }

    /** remove car name from polyparams */
    ['Name', 'Subtype', 'Type'].forEach(prop => delete postData.Car[prop]);

    if (profileId) {
      postData.UserId = profileId;
    }

    this.addWidgetKey(postData);

    const VqrzrxFGyHNUVIRU = this.VqrzrxFGyHNUVIRU(postData);
    const headers = new HttpHeaders({
      'Vqrzrxfgyhnuviru': VqrzrxFGyHNUVIRU.toString()
    });

    return this.http.post(ROUTING_URL, window.btoa(JSON.stringify(postData)), { headers: headers });
  }

  /** In widget mode the range and routing calls carry the widget's `api_key` as `key` in the (hashed) post data. */
  private addWidgetKey(postData: any) {
    const key = this.widgetService.isWidget() ? this.widgetService.getApiKey() : null;
    if (key) {
      postData.key = key;
    }
  }

  public getLanguageJSON(languageSettings: string) {
    return this.http.get(`${LANGUAGE_JSON_URL.replace("$lang", languageSettings)}?ver=${languageJsonVersion}`);
  }

  public getCountryListJSON(languageSettings: string) {
    return this.http.get(COUNTRYLIST_JSON_URL.replace("$lang", languageSettings));
  }

  public getVersion() {
    return this.http.get(VERSION_URL, { responseType: 'text' });
  }

  public getVehiclesVersion() {
    return this.http.get(VEHICLES_VERSION_URL, { responseType: 'text' });
  }

  public getAllVehicles() {
    return this.http.get(ALL_VEHICLES_URL);
  }

  /** Widget `api_key` check — answers `{ success|failed: { valid, key } }` (see WidgetAuthService). */
  public validateWidgetApiKey(key: string) {
    const headers = new HttpHeaders({ 'Content-Type': 'application/json', 'Accept': 'application/json' });
    return this.http.get(WIDGET_KEY_VALIDATION_URL, { headers, params: { key } }).pipe(timeout(HTTP_TIMEOUT));
  }

  public getChargingOperators(version: string) {
    return this.http.get(CHARGING_OPERATORS_URL.replace("$ver", version));
  }

  public getCheck() {
    return this.http.get(CHECK_URL, { responseType: 'text' });
  }

  public getReverseGeocode(lat: number, lon: number) {
    return this.http.get(REVERSE_GEOCODE_URL.replace("$lat", lat.toString()).replace("$lon", lon.toString()));
  }

  public getChargingTile(typeFlag: string, tileId: string, hash: string, chargingStationVersion: string) {
    return this.http.get(CHARGING_TILE_URL.replace("$typeFlag", typeFlag).replace("$tileId", tileId).replace("$hash", hash)
      .replace("$chargingStationVersion", chargingStationVersion), { responseType: 'arraybuffer' });
  }

  public getTurnAudioFile(voiceLib: string, turnName: string) {
    return this.http.get(TURN_AUDIO_URL.replace("$voiceLib", voiceLib).replace("$turnName", turnName), { responseType: "arraybuffer" });
  }

  public getUserCountry(): Observable<any> {
    return this.http.get(USER_COUNTRY_URL);
  }

  public getUser(): Observable<any> {
    return this.http.get(USER_URL).pipe(timeout(HTTP_TIMEOUT));
  }

  public getUserVehicles(): Observable<any> {
    return this.http.get(USER_VEHICLES_URL);
  }

  public getUserSettings(): Observable<any> {
    return this.http.get(USER_SETTINGS_URL).pipe(timeout(HTTP_TIMEOUT));
  }

  public updateUserSettings(unit: UnitSettings, routing: RoutingSettings, voiceguidance: VoiceGuidenceSettings, mapsettings: MapSettings,
    voiceInputTutorialDone: boolean, tracking: boolean, language: string): Observable<any> {
    const formData = new FormData();
    if (unit != undefined && unit != null) { formData.append("unit", JSON.stringify(unit).toLowerCase()); }
    if (routing != undefined && routing != null) { formData.append("routing", JSON.stringify(routing).toLowerCase()); }
    if (voiceguidance != undefined && voiceguidance != null) { formData.append("voiceguidance", JSON.stringify(voiceguidance).toLowerCase()); }
    if (mapsettings != undefined && mapsettings != null) { formData.append("mapsettings", JSON.stringify(mapsettings).toLowerCase()); }
    if (voiceInputTutorialDone != undefined && voiceInputTutorialDone != null) { formData.append("voiceInputTutorialDone", JSON.stringify(voiceInputTutorialDone).toLowerCase()); }
    if (tracking != undefined && tracking != null) { formData.append("tracking", JSON.stringify(tracking).toLowerCase()); }
    if (language) { formData.append("language", language); };

    return this.http.post(USER_SETTINGS_URL, formData).pipe(timeout(HTTP_TIMEOUT));
  }

  public checkUserExist(email: string): Observable<any> {
    return this.http.post(USER_EXIST_URL, { email }).pipe(timeout(HTTP_TIMEOUT));
  }

  public registerUser(profile: UserProfile, password: string, language: string): Observable<any> {
    const formData = new FormData();
    formData.append("first_name", profile.FirstName);
    formData.append("last_name", profile.LastName);
    formData.append("email", profile.Email);
    formData.append("password", password);
    formData.append("c_password", password);
    formData.append("country", profile.Country);
    formData.append("phone_number", profile.Phone.toString());
    formData.append("newsletter", profile.Newsletter ? "1" : "0");
    formData.append("language", language);
    if (profile.Avatar) {
      formData.append("avatar", profile.Avatar);
    }

    return this.http.post(REGISTER_URL, formData).pipe(timeout(HTTP_TIMEOUT));
  }

  public verifyEmail(urlParam: string): Observable<any> {
    return this.http.get(urlParam).pipe(timeout(HTTP_TIMEOUT));
  }

  public resendEmail(email: string): Observable<any> {
    return this.http.post(RESEND_URL, { email }).pipe(timeout(HTTP_TIMEOUT));
  }

  public authenticateSocial(provider: string, providerId: string, accessToken: string, isGPSTWebview: boolean, native?: boolean): Observable<any> {
    const formData = new FormData();
    formData.append("provider", provider);
    if (provider === "apple") {
      let user = JSON.parse(providerId);
      formData.append("provider_id", user.userID.toString());
      formData.append("user", JSON.stringify({ "name": { "firstName": user.name.firstname, "lastName": user.name.lastname }, "email": user.email }));
    }
    else {
      formData.append("provider_id", providerId);
    }
    formData.append("access_token", accessToken);
    formData.append("native", native ? native.toString() : "null");

    const url = provider === "google" && !isGPSTWebview ? REGISTER_EXTERNAL_GOOGLE : REGISTER_EXTERNAL;

    return this.http.post(url, formData).pipe(timeout(HTTP_TIMEOUT));
  }

  public loginUser(email: string, password: string): Observable<any> {
    const formData = new FormData();
    formData.append("email", email);
    formData.append("password", password);

    return this.http.post(LOGIN_URL, formData).pipe(timeout(HTTP_TIMEOUT));
  }

  public updateUserProfile(profile: UserProfile): Observable<any> {
    const formData = new FormData();
    formData.append("first_name", profile.FirstName);
    formData.append("last_name", profile.LastName);
    formData.append("country", profile.Country);
    formData.append("phone_number", profile.Phone.toString());
    formData.append("newsletter", profile.Newsletter ? "1" : "0");
    if (profile.Avatar && typeof (profile.Avatar) == "object") {
      formData.append("avatar", profile.Avatar);
    }
    if (profile.Avatar == "delete") {
      formData.append("avatar", "DELETE");
    }

    return this.http.post(USER_PROFILE_URL, formData).pipe(timeout(HTTP_TIMEOUT));
  }

  public getUserSubscriptionUrl(url: string): string {
    return url + `?token=${this.tokenStore.get()}`;
  }

  public cancelUserSubscription(): Observable<any> {
    return this.http.get(CANCEL_USER_SUBSCRIPTION).pipe(timeout(HTTP_TIMEOUT));
  }

  public addUserVehicle(vehicleId: number, vehicleName: string, chargerTypes: number[], settings: any): Observable<any> {
    const postData = {
      vehicle_id: vehicleId,
      name: vehicleName,
      charger_types: "[" + chargerTypes.toString() + "]",
      settings: JSON.stringify(settings)
    };

    return this.http.post(USER_VEHICLE_ADD_URL, postData).pipe(timeout(HTTP_TIMEOUT));
  }

  public selectUserVehicle(vehicleId: number): Observable<any> {
    return this.http.post(USER_VEHICLE_SELECT_URL, { user_vehicle_id: vehicleId }).pipe(timeout(HTTP_TIMEOUT));
  }

  public modifyUserVehicle(userVehicleId: number, vehicleId: number | null, vehicleName: string | null, chargerTypes: number[] | null, settings: string): Observable<any> {
    const postData = {
      user_vehicle_id: userVehicleId,
      // null = leave the name untouched; '' is sent so a custom name can be cleared.
      ...(vehicleName !== null && { name: vehicleName }),
      ...(chargerTypes && { charger_types: `[${chargerTypes}]` }),
      ...(vehicleId && { vehicle_id: vehicleId }),
      ...(settings && { settings })
    };

    return this.http.post(USER_VEHICLE_MODIFY_URL, postData).pipe(timeout(HTTP_TIMEOUT));
  }

  public removeUserVehicle(vehicleId: string): Observable<any> {
    return this.http.post(USER_VEHICLE_REMOVE_URL, { user_vehicle_id: vehicleId }).pipe(timeout(HTTP_TIMEOUT));
  }

  public changeUserPassword(emailAddress: string, currentPassword: string, newPassword: string, cNewPassword: string): Observable<any> {
    const formData = new FormData();
    formData.append("email", emailAddress);
    formData.append("current_password", currentPassword);
    formData.append("new_password", newPassword);
    formData.append("new_confirm_password", cNewPassword);

    return this.http.post(USER_CHANGE_PASSWORD_URL, formData).pipe(timeout(HTTP_TIMEOUT));
  }

  public requestPasswordReset(email: string): Observable<any> {
    return this.http.post(RESET_REQUEST_URL, { email }).pipe(timeout(HTTP_TIMEOUT));
  }

  public resetPassword(token: string, email: string, newPassword: string, cNewPassword: string): Observable<any> {
    const formData = new FormData();
    formData.append("token", token);
    formData.append("email", email);
    formData.append("password", newPassword);
    formData.append("password_confirmation", cNewPassword);
    return this.http.post(RESET_PASSWORD_URL, formData).pipe(timeout(HTTP_TIMEOUT));
  }

  public updateUserAddress(addresses: Address[]): Observable<any> {
    const formData = new FormData();
    formData.append("addresses", JSON.stringify(addresses));

    return this.http.post(USER_ADDRESS_URL, formData).pipe(timeout(HTTP_TIMEOUT));
  }

  public deleteUser(): Observable<any> {
    return this.http.post(USER_DELETE_URL, {}).pipe(timeout(HTTP_TIMEOUT));
  }

  public deleteUserConfirm(token: string): Observable<any> {
    return this.http.get(USER_DELETE_CONFIRM_URL.replace("$token", token)).pipe(timeout(HTTP_TIMEOUT));
  }

  public getEvnaviActivities(carId: string): Observable<any> {
    return this.http.post(EVNAVI_GET_ACTIVITIES_URL.replace("$carId", carId), null);
  }

  public readEvnaviFiles(logId: string): Observable<any> {
    return this.http.post(EVNAVI_READ_FILES_URL.replace("$logId", logId), null);
  }

  public logEvnaviData(userVehicleId: string, data: any): Observable<any> {
    return this.http.post(EVNAVI_LOG_URL.replace("$userVehicleId", userVehicleId), { geojson: data });
  }

  public deleteEvnaviLogById(logId: string): Observable<any> {
    return this.http.post(EVNAVI_DELETE_LOG_BY_ID_URL.replace("$logId", logId), null, { responseType: "text" });
  }

  public deleteEvnaviLogByVehicle(carId: string): Observable<any> {
    return this.http.post(EVNAVI_DELETE_LOG_BY_VEHICLE_URL.replace("$carId", carId), null, { responseType: "text" });
  }

  public updateDisabledChargingStations(ids: string[]): Observable<any> {
    const formData = new FormData();
    formData.append("ids", JSON.stringify(ids));
    return this.http.post(UPDATE_DISABLED_CHARGING_STATIONS, formData).pipe(timeout(HTTP_TIMEOUT));
  }

  public updateFavoriteChargingStations(ids: string[]): Observable<any> {
    const formData = new FormData();
    formData.append("ids", JSON.stringify(ids));
    return this.http.post(UPDATE_FAVORITE_CHARGING_STATIONS, formData).pipe(timeout(HTTP_TIMEOUT));
  }

  public updateDisabledChargingOperators(ids: number[]): Observable<any> {
    const formData = new FormData();
    formData.append("ids", JSON.stringify(ids));
    return this.http.post(UPDATE_DISABLED_CHARGING_OPERATORS, formData).pipe(timeout(HTTP_TIMEOUT));
  }

  public updateFavoriteChargingOperators(ids: number[]): Observable<any> {
    const formData = new FormData();
    formData.append("ids", JSON.stringify(ids));
    return this.http.post(UPDATE_FAVORITE_CHARGING_OPERATORS, formData).pipe(timeout(HTTP_TIMEOUT));
  }

  public voucherCheck(voucherId: string): Observable<any> {
    const formData = new FormData();
    formData.append("voucher_id", voucherId);
    return this.http.post(VOUCHER_CHECK, formData).pipe(timeout(HTTP_TIMEOUT));
  }

  public addVoucherSubscription(voucherId: string): Observable<any> {
    const formData = new FormData();
    formData.append("voucher_id", voucherId);
    return this.http.post(ADD_VOUCHER_SUBSCRIPTION, formData).pipe(timeout(HTTP_TIMEOUT));
  }

  public subscribeToSubscription(billingAddress: BillingAddress, business: Business | null, product: Product): Observable<any> {
    const postData = {
      billing_address: billingAddress,
      business: business,
      product: product
    };

    return this.http.post(SUBSCRIBE_TO_SUBSCRIPTION, postData).pipe(timeout(HTTP_TIMEOUT));
  }

  public getVATEUCheck(vatNumber: string, countryCode: string): Observable<any> {
    const url = VAT_CHECK.replace("$vatNumber", vatNumber).replace("$countryCode", countryCode);
    return this.http.get(url);
  }

  public sendFeedbackEmail(payload: {
    captchaResponse: string;
    email: string;
    rating: number;
    reviewText: string;
    userAgent?: string | null;
    inputParams?: string | null;
    userProfileJson?: string | null;
  }) {
    const emailJson: any = {
      captchaResponse: payload.captchaResponse,
      email: payload.email,
      rating: payload.rating,
      reviewText: payload.reviewText,
      userAgent: payload.userAgent || null,
      inputParams: payload.inputParams || null
    };

    if (payload.userProfileJson) {
      emailJson.inputParams = (emailJson.inputParams || '') + '\r\n' + payload.userProfileJson;
    }

    return this.http.post(SEND_FEEDBACK_EMAIL_URL, emailJson);
  }

  public getChargingStationDetails(chargingStationIds: string[]) {
    const postData = { id: chargingStationIds.map((el) => { return el.toLocaleLowerCase() }) };
    return this.http.post(CHARGING_STATION_DETAIL_URL, postData);
  }

  public getChargingStationStatus(latMin: number, latMax: number, lonMin: number, lonMax: number, usedPlugTypes: number[]) {
    const postData = JSON.stringify({ "latMin": latMin, "latMax": latMax, "lonMin": lonMin, "lonMax": lonMax, "usedPlugTypes": usedPlugTypes });
    return this.http.post(CHARGING_STATION_STATUS_URL, postData);
  }

  public getFavoriteChargingStationStatus(chargingStationIds: string[]) {
    const postData = JSON.stringify({ "id": chargingStationIds });
    return this.http.post(CHARGING_STATION_STATUS_URL, postData);
  }

  public getExportPersonalData(): Observable<any> {
    return this.http.get(`${EXPORT_PERSONAL_DATA}?token=${this.tokenStore.get()}`, { responseType: 'text' });
  }

  public initVoiceCommandAudio(turnList: { id: string, name: string, icon: string, sound: string }[]) {
    // loading is delayed to not overload device with too many calls
    timer(4000).pipe(
      concatMap(() => from(VOICE_LIBS)),
      concatMap((voiceLib, i) =>
        timer(3000 * i).pipe(
          concatMap(() => from(turnList).pipe(
            concatMap(turn => turn.sound ? this.getTurnAudioFile(voiceLib, turn.sound) : [])
          ))
        )
      )
    ).subscribe();
  }

  public getAudioPlayList(audioPlayList: string[]) {
    return new Observable((observer: Observer<ArrayBuffer[]>) => {
      let playlistHttpCalls = audioPlayList.map((el) => { return this.http.get(el, { responseType: 'arraybuffer' }) });
      forkJoin(playlistHttpCalls).subscribe({
        next: (resp: ArrayBuffer[]) => {
          observer.next(resp);
          observer.complete();
        },
        error: (error) => {
          observer.next([]);
          observer.complete();
        }
      });
    });
  }

  public VqrzrxFGyHNUVIRU(polyParams) {
    let SXihtOZnuC = new Md5();
    let cleanStr = this.cleanString(JSON.stringify(polyParams));
    SXihtOZnuC.appendStr(cleanStr + "OLiFNGTFc5T92WNGSRgBnpcG4crUlTg7tFII0YoNZiDPJSLcnaI5sSwU4jMw5V21");
    return SXihtOZnuC.end();
  }

  public cleanString(input: string): string {
    let output = "";
    for (let i = 0; i < input.length; i++) {
      if (input.charCodeAt(i) <= 127) {
        output += input.charAt(i);
      }
    }
    return output;
  }
}
